import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Scissors, Check, Loader2, Home, MapPin, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { SAME_HIVE_METHODS, methodFor, createSplitReminders } from '../utils/splitMethods';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPLIT_METHODS = [
  { value: 'walk-away', label: 'Walk-away Split' },
  { value: 'queen-cell', label: 'Queen Cell Split' },
  { value: 'demaree', label: 'Demaree Method' },
  { value: 'double_screen', label: 'Double Screen Board' },
  { value: 'nuc-split', label: 'Nuc Split' },
  { value: 'equal-split', label: 'Equal Split' },
  { value: 'cutdown', label: 'Cutdown Split' },
  { value: 'taranov', label: 'Taranov Method' },
  { value: 'custom', label: 'Custom' },
];

const QUEEN_STATUS = [
  { value: 'queen-right', label: 'Queen-right (has queen)' },
  { value: 'queenless', label: 'Queenless (will raise queen)' },
  { value: 'queen-cell', label: 'Queen cell given' },
];

const canKeep = (m) => SAME_HIVE_METHODS.includes(m);

const BulkSplit = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [step, setStep] = useState(1);
  const [yards, setYards] = useState([]);
  const [hives, setHives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Step 1: Event details
  const [selectedYard, setSelectedYard] = useState('');
  const [splitDate, setSplitDate] = useState(new Date().toISOString().split('T')[0]);
  const [defaultMethod, setDefaultMethod] = useState('walk-away');
  const [notes, setNotes] = useState('');

  // Step 2: Selected hives
  const [selectedHives, setSelectedHives] = useState(new Set());

  // Step 3: Per-hive config
  const [splitConfig, setSplitConfig] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (!token && localStorage.getItem('demoMode') === 'true') {
        setLoading(false);
        return;
      }
      try {
        const [yardsRes, hivesRes] = await Promise.all([
          axios.get(`${API}/yards`, { headers }),
          axios.get(`${API}/hives`, { headers }),
        ]);
        setYards(yardsRes.data || []);
        setHives((hivesRes.data || []).filter(h => h.status === 'active'));
      } catch (err) { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const yardHives = selectedYard
    ? hives.filter(h => h.yard === selectedYard || h.yard === yards.find(y => y.id === selectedYard)?.name)
    : hives;

  const toggleHive = (hiveId) => {
    setSelectedHives(prev => {
      const next = new Set(prev);
      if (next.has(hiveId)) next.delete(hiveId);
      else next.add(hiveId);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedHives.size === yardHives.length) {
      setSelectedHives(new Set());
    } else {
      setSelectedHives(new Set(yardHives.map(h => h.id)));
    }
  };

  const goToStep3 = () => {
    // Initialize split config for selected hives
    const config = {};
    selectedHives.forEach(hiveId => {
      const hive = hives.find(h => h.id === hiveId);
      const num = hive?.hive_number || '?';
      // Count existing daughters
      const existingDaughters = hives.filter(h => h.split_from_hive === hiveId).length;
      const suffix = String.fromCharCode(97 + existingDaughters);
      config[hiveId] = {
        parent_hive_number: num,
        method: defaultMethod,
        queen_status: 'queenless',
        keep_in_parent: canKeep(defaultMethod),
        daughter_display: `${num}${suffix}`,
      };
    });
    setSplitConfig(config);
    setStep(3);
  };

  const updateConfig = (hiveId, field, value) => {
    setSplitConfig(prev => ({
      ...prev,
      [hiveId]: { ...prev[hiveId], [field]: value, ...(field === 'method' && canKeep(value) && !canKeep(prev[hiveId]?.method) ? { keep_in_parent: true } : {}) }
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const splits = Object.entries(splitConfig).map(([hiveId, cfg]) => ({
        parent_hive_id: hiveId,
        parent_hive_number: cfg.parent_hive_number,
        method: cfg.method,
        queen_status: cfg.queen_status,
        keep_in_parent: canKeep(cfg.method) && cfg.keep_in_parent,
      }));

      const yardName = yards.find(y => y.id === selectedYard)?.name || selectedYard;

      const res = await axios.post(`${API}/bulk-splits`, {
        yard_id: selectedYard,
        yard_name: yardName,
        split_date: splitDate,
        default_method: defaultMethod,
        notes,
        splits,
      }, { headers });

      // Step reminders for each split, on the hive that holds it
      let reminders = 0;
      for (const sp of res.data.splits || []) {
        const method = methodFor(sp.method);
        if (!method) continue;
        const holder = sp.kept_in_parent ? hives.find(h => h.id === sp.parent_hive_id) : res.data.created_hives?.find(h => h.id === sp.destination_hive_id);
        reminders += await createSplitReminders({ method, hiveId: sp.destination_hive_id, hiveNumber: holder?.hive_number,
          displayNumber: holder?.display_number, yard: holder?.yard || yardName, splitId: sp.id, startDate: `${splitDate}T12:00:00`, headers });
      }
      setResult({ ...res.data, reminders });
      setStep(4);
      window.dispatchEvent(new Event('refreshData'));
      toast.success(`${res.data.total_splits} hives split successfully!`, { description: reminders ? `${reminders} reminders added to Tasks and Calendar.` : undefined });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create bulk split');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="honeycomb-bg flex items-center justify-center"><Loader2 className="animate-spin h-8 w-8 text-amber-500" /></div>;

  return (
    <div className="pb-24" data-testid="bulk-split-page">
      <div className="max-w-7xl p-4 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
            <Scissors className="h-6 w-6 text-amber-600" />
            Bulk Split
          </h1>
          <p className="text-sm text-stone-500 mt-1">Split multiple hives at once — Step {step} of 3</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2">
          {['Event Details', 'Select Hives', 'Review & Adjust', 'Complete'].map((label, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-amber-500 text-white' : 'bg-stone-200 text-stone-500'
              }`}>
                {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:inline ${step === i + 1 ? 'text-amber-700 font-bold' : 'text-stone-400'}`}>{label}</span>
              {i < 3 && <div className={`flex-1 h-0.5 ${step > i + 1 ? 'bg-green-400' : 'bg-stone-200'}`} />}
            </div>
          ))}
        </div>

        {/* STEP 1: Event Details */}
        {step === 1 && (
          <Card className="bg-white">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-bold text-stone-800 text-lg">Split Event Details</h2>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1 block">Yard</label>
                  <select value={selectedYard} onChange={e => setSelectedYard(e.target.value)}
                    className="w-full h-10 border rounded-lg px-3 text-sm" data-testid="split-yard-select">
                    <option value="">All Yards</option>
                    {yards.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1 block">Split Date</label>
                  <Input type="date" value={splitDate} onChange={e => setSplitDate(e.target.value)} data-testid="split-date" />
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1 block">Default Split Method</label>
                  <select value={defaultMethod} onChange={e => setDefaultMethod(e.target.value)}
                    className="w-full h-10 border rounded-lg px-3 text-sm" data-testid="split-method">
                    {SPLIT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <p className="text-xs text-stone-400 mt-1">Applied to all hives — you can change individual ones in Step 3</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1 block">Notes</label>
                  <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Spring split round..." />
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setStep(2)} className="bg-amber-500 hover:bg-amber-600">
                  Next: Select Hives <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 2: Select Hives */}
        {step === 2 && (
          <Card className="bg-white">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-stone-800 text-lg">Select Hives to Split</h2>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-stone-500">{selectedHives.size} of {yardHives.length} selected</span>
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    {selectedHives.size === yardHives.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
              </div>

              {yardHives.length === 0 ? (
                <div className="text-center py-8 text-stone-400">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2" />
                  <p>No active hives found{selectedYard ? ' in this yard' : ''}.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {yardHives.map(hive => {
                    const selected = selectedHives.has(hive.id);
                    return (
                      <button key={hive.id} onClick={() => toggleHive(hive.id)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          selected ? 'border-amber-500 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'
                        }`} data-testid={`split-hive-${hive.hive_number}`}>
                        <div className="flex items-center gap-2">
                          <div className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                            selected ? 'bg-amber-500 border-amber-500' : 'border-stone-300'
                          }`}>
                            {selected && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <Home className="h-4 w-4 text-stone-400" />
                        </div>
                        <div className="mt-2">
                          <span className="font-bold text-stone-800">Hive {hive.nickname || hive.hive_number}</span>
                          {hive.yard && <p className="text-xs text-stone-400 truncate">{hive.yard}</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={goToStep3} disabled={selectedHives.size === 0} className="bg-amber-500 hover:bg-amber-600">
                  Next: Review ({selectedHives.size} hives) <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 3: Review & Adjust */}
        {step === 3 && (
          <Card className="bg-white">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-bold text-stone-800 text-lg">Review & Adjust Splits</h2>
              <p className="text-sm text-stone-500">Each row creates a new daughter hive, unless a Demaree or Double Screen row is kept in the same hive. Adjust methods or queen status as needed.</p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-stone-50 border-b">
                      <th className="text-left py-3 px-3">Parent Hive</th>
                      <th className="text-left py-3 px-3">Split Method</th>
                      <th className="text-left py-3 px-3">Queen Status (daughter)</th>
                      <th className="text-center py-3 px-3">Where it goes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(splitConfig).map(([hiveId, cfg]) => (
                      <tr key={hiveId} className="border-b hover:bg-stone-50">
                        <td className="py-3 px-3">
                          <span className="font-bold text-stone-800">Hive {cfg.parent_hive_number}</span>
                        </td>
                        <td className="py-3 px-3">
                          <select value={cfg.method} onChange={e => updateConfig(hiveId, 'method', e.target.value)}
                            className="h-9 border rounded-lg px-2 text-sm w-full max-w-[200px]">
                            {SPLIT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                          </select>
                        </td>
                        <td className="py-3 px-3">
                          <select value={cfg.queen_status} onChange={e => updateConfig(hiveId, 'queen_status', e.target.value)}
                            className="h-9 border rounded-lg px-2 text-sm w-full max-w-[220px]">
                            {QUEEN_STATUS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                          </select>
                        </td>
                        <td className="py-3 px-3 text-center">
                          {canKeep(cfg.method) && (
                            <label className="flex items-center justify-center gap-2 text-xs text-stone-600 mb-1 cursor-pointer">
                              <input type="checkbox" checked={!!cfg.keep_in_parent} onChange={e => updateConfig(hiveId, 'keep_in_parent', e.target.checked)}
                                data-testid={`bulk-split-keep-${hiveId}`} />
                              Keep in same hive
                            </label>
                          )}
                          {canKeep(cfg.method) && cfg.keep_in_parent ? (
                            <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-bold text-xs">Boost Hive {cfg.parent_hive_number}</span>
                          ) : (
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-mono font-bold text-sm">
                              {cfg.daughter_display}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h3 className="font-bold text-amber-800 text-sm mb-1">Summary</h3>
                <p className="text-sm text-amber-700">
                  {(() => {
                    const total = Object.keys(splitConfig).length;
                    const kept = Object.values(splitConfig).filter(c => canKeep(c.method) && c.keep_in_parent).length;
                    return `${total} hive${total === 1 ? '' : 's'} will be split, creating ${total - kept} new daughter hive${total - kept === 1 ? '' : 's'}${kept ? ` and boosting ${kept} in place` : ''}. Each daughter inherits the parent's yard and gets an initial inspection record. Step reminders are added to Tasks and Calendar.`;
                  })()}
                </p>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">
                  {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Splits...</> : <><Scissors className="mr-2 h-4 w-4" /> Confirm & Split</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 4: Complete */}
        {step === 4 && result && (
          <Card className="bg-white">
            <CardContent className="p-8 text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-stone-900">Bulk Split Complete!</h2>
                <p className="text-stone-500 mt-2" data-testid="bulk-split-result-summary">
                  {result.created_hives?.length || 0} new hive{result.created_hives?.length === 1 ? '' : 's'} created
                  {(result.splits || []).some(sp => sp.kept_in_parent) && `, ${(result.splits || []).filter(sp => sp.kept_in_parent).length} kept in the same hive`}
                  {result.reminders ? `. ${result.reminders} reminders added to Tasks and Calendar.` : ''}
                </p>
              </div>

              <div className="max-w-md mx-auto space-y-2">
                {result.created_hives?.map(h => (
                  <div key={h.id} className="flex items-center justify-between bg-stone-50 rounded-lg px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-stone-400" />
                      <span className="font-mono font-bold text-green-700">{h.display_number}</span>
                    </div>
                    <span className="text-xs text-stone-500">from Hive {h.parent_hive_number} — {h.method.replace('-', ' ')}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/')}>
                  Back to Dashboard
                </Button>
                <Button onClick={() => { setStep(1); setSelectedHives(new Set()); setSplitConfig({}); setResult(null); }} className="bg-amber-500 hover:bg-amber-600">
                  Split More Hives
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BulkSplit;
